import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, MessageSquare, RefreshCw, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DocumentSheetDialog } from '@/components/documents/document-sheet-dialog';
import { cn } from '@/lib/cn';
import { newIdempotencyKey } from '@/lib/uuid';
import {
  flightTicketApi,
  flightTicketError,
  flightTicketKeys,
  type FlightTicketKind,
  type TicketDelivery,
} from '@/lib/flight-ticket/api';
import { FlightTicketSheet } from './flight-ticket-sheet';

const BRAND = (import.meta.env.VITE_TENANT ?? 'nexa').toUpperCase();

/** The upstream's safe code for a failed read, resolved once per error. */
function useErrorCode(error: unknown): string | null {
  const [code, setCode] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setCode(null);
    if (error) void flightTicketError(error).then((value) => live && setCode(value));
    return () => {
      live = false;
    };
  }, [error]);
  return code;
}

/**
 * «Ver billete»: the e-ticket of a confirmed flight purchase, read live from the airline
 * order only while the sheet is open (nothing is cached across sessions — it carries names),
 * printable/PDF, with the delivery history to the passenger and a supervisor's «Reenviar».
 */
export function ViewTicketButton({
  kind,
  id,
  canResend,
  size = 'default',
}: {
  kind: FlightTicketKind;
  id: string;
  canResend: boolean;
  size?: 'default' | 'sm';
}) {
  const { t } = useTranslation('flightTicket');
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size={size} onClick={() => setOpen(true)} data-testid="view-ticket">
        <Ticket className="h-4 w-4" aria-hidden />
        {t('view')}
      </Button>
      {open ? <FlightTicketDialog kind={kind} id={id} canResend={canResend} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function FlightTicketDialog({ kind, id, canResend, onClose }: { kind: FlightTicketKind; id: string; canResend: boolean; onClose: () => void }) {
  const { t } = useTranslation('flightTicket');
  const ticket = useQuery({
    queryKey: flightTicketKeys.ticket(kind, id),
    queryFn: () => flightTicketApi.ticket(kind, id),
    retry: false,
    gcTime: 0,
  });
  const ticketError = useErrorCode(ticket.error);
  const errorText = ticket.error
    ? ticketError
      ? t(`errors.${ticketError}`, { defaultValue: t('errors.generic', { code: ticketError }) })
      : t('errors.generic', { code: '…' })
    : null;
  return (
    <DocumentSheetDialog
      open
      onClose={onClose}
      title={t('title')}
      description={t('description')}
      ready={!!ticket.data}
      error={errorText}
      testId="flight-ticket-dialog"
      aside={<TicketDeliveries kind={kind} id={id} canResend={canResend} sandbox={ticket.data?.mode === 'sandbox'} />}
    >
      {ticket.data ? <FlightTicketSheet ticket={ticket.data} brand={BRAND} /> : null}
    </DocumentSheetDialog>
  );
}

const STATUS_TONE: Record<TicketDelivery['status'], string> = {
  SENT: 'text-emerald-700 dark:text-emerald-400',
  PENDING: 'text-muted-foreground',
  FAILED: 'text-rose-700 dark:text-rose-400',
  SKIPPED: 'text-amber-700 dark:text-amber-400',
};

export function TicketDeliveries({ kind, id, canResend, sandbox }: { kind: FlightTicketKind; id: string; canResend: boolean; sandbox: boolean }) {
  const { t, i18n } = useTranslation('flightTicket');
  const cache = useQueryClient();
  const key = flightTicketKeys.deliveries(kind, id);
  // One read when the sheet opens; a resend answers with the fresh history itself.
  const list = useQuery({ queryKey: key, queryFn: () => flightTicketApi.deliveries(kind, id), retry: false });
  const listCode = useErrorCode(list.error);
  const [resendCode, setResendCode] = useState<string | null>(null);
  const resend = useMutation({
    mutationFn: () => flightTicketApi.resend(kind, id, newIdempotencyKey()),
    onMutate: () => setResendCode(null),
    onSuccess: (result) => cache.setQueryData(key, { deliveries: result.deliveries }),
    onError: async (error) => setResendCode(await flightTicketError(error)),
  });
  const rows = list.data?.deliveries ?? [];
  const when = (at: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(at));
  return (
    <section className="rounded-lg border border-border bg-card p-4 text-card-foreground shadow-sm" aria-label={t('deliveries.title')} data-testid="ticket-deliveries">
      <header className="flex flex-wrap items-center gap-3">
        <h3 className="text-sm font-semibold">{t('deliveries.title')}</h3>
        <div className="ml-auto">
          {canResend ? (
            <Button size="sm" variant="outline" disabled={resend.isPending} onClick={() => resend.mutate()} data-testid="ticket-resend">
              <RefreshCw className={cn('h-3.5 w-3.5', resend.isPending && 'animate-spin')} aria-hidden />
              {t(resend.isPending ? 'deliveries.resending' : 'deliveries.resend')}
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">{t('deliveries.supervisorOnly')}</span>
          )}
        </div>
      </header>
      {sandbox ? <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">{t('deliveries.sandboxNote')}</p> : null}
      {resendCode ? <p className="mt-2 text-sm text-destructive" role="alert">{t('deliveries.resendError', { code: resendCode })}</p> : null}
      {list.isPending ? (
        <p className="mt-3 text-sm text-muted-foreground" aria-busy="true">{t('deliveries.loading')}</p>
      ) : list.error ? (
        <p className="mt-3 text-sm text-destructive" role="alert">{t('deliveries.error', { code: listCode ?? '…' })}</p>
      ) : !rows.length ? (
        <p className="mt-3 text-sm text-muted-foreground" data-testid="ticket-deliveries-empty">{t('deliveries.empty')}</p>
      ) : (
        <ul className="mt-3 divide-y divide-border text-sm">
          {rows.map((row, index) => {
            const Icon = row.channel === 'EMAIL' ? Mail : MessageSquare;
            return (
              <li key={`${row.at}-${row.channel}-${index}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2" data-testid="ticket-delivery">
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="font-medium">{t(`deliveries.channel.${row.channel}`)}</span>
                {row.destination ? <span className="font-mono text-xs">{row.destination}</span> : null}
                <span className={cn('font-medium', STATUS_TONE[row.status])}>
                  {t(`deliveries.status.${row.status}`)}
                  {row.simulated && row.status === 'SENT' ? ` (${t('deliveries.simulated')})` : ''}
                </span>
                {row.code ? (
                  <span className="text-xs text-muted-foreground">
                    {t(`deliveries.code.${row.code}`, { defaultValue: row.code })}
                  </span>
                ) : null}
                <span className="ml-auto text-xs text-muted-foreground">
                  {t(row.trigger === 'auto' ? 'deliveries.auto' : 'deliveries.manual')} · {when(row.at)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
